// src/contexts/GoogleCalendarContext.jsx
import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { auth } from '../services/firebase';
import { fetchGoogleCalendarEvents } from '../services/googleCalendar';
import toast from 'react-hot-toast';

const STORAGE_KEY_TOKEN = 'gcal_access_token';
const STORAGE_KEY_EXPIRY = 'gcal_token_expiry';

const GoogleCalendarContext = createContext();

export const useGoogleCalendar = () => {
  const context = useContext(GoogleCalendarContext);
  if (!context) {
    throw new Error('useGoogleCalendar must be used within GoogleCalendarProvider');
  }
  return context;
};

export const GoogleCalendarProvider = ({ children }) => {
  const [accessToken, setAccessToken] = useState(null);
  const [tokenExpiry, setTokenExpiry] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [googleEvents, setGoogleEvents] = useState([]);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [syncError, setSyncError] = useState(null);
  const expiryTimerRef = useRef(null);

  // Al montar: restaurar token desde localStorage si no ha expirado
  useEffect(() => {
    const savedToken = localStorage.getItem(STORAGE_KEY_TOKEN);
    const savedExpiry = localStorage.getItem(STORAGE_KEY_EXPIRY);

    if (savedToken && savedExpiry) {
      const expiry = Number(savedExpiry);
      if (Date.now() < expiry) {
        setAccessToken(savedToken);
        setTokenExpiry(expiry);
        setIsConnected(true);

        // Programar expiración del token restante
        const remaining = expiry - Date.now();
        expiryTimerRef.current = setTimeout(() => {
          handleTokenExpired();
        }, remaining);
      } else {
        // Token expirado, limpiar
        localStorage.removeItem(STORAGE_KEY_TOKEN);
        localStorage.removeItem(STORAGE_KEY_EXPIRY);
      }
    }

    return () => {
      if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
    };
  }, []);

  const handleTokenExpired = useCallback(() => {
    setIsConnected(false);
    setAccessToken(null);
    setTokenExpiry(null);
    localStorage.removeItem(STORAGE_KEY_TOKEN);
    localStorage.removeItem(STORAGE_KEY_EXPIRY);
    setSyncError('Token expirado. Reconecta Google Calendar.');
    toast('Token de Google Calendar expirado', { icon: '🔄' });
  }, []);

  const isTokenExpired = useCallback(() => {
    if (!tokenExpiry) return true;
    return Date.now() >= tokenExpiry;
  }, [tokenExpiry]);

  const connectCalendar = useCallback(async () => {
    setIsConnecting(true);
    setSyncError(null);
    try {
      const provider = new GoogleAuthProvider();
      provider.addScope('https://www.googleapis.com/auth/calendar.readonly');
      provider.setCustomParameters({ prompt: 'consent' });

      const result = await signInWithPopup(auth, provider);
      const credential = GoogleAuthProvider.credentialFromResult(result);

      if (!credential || !credential.accessToken) {
        throw new Error('No se obtuvo el token de acceso de Google');
      }

      const token = credential.accessToken;
      const expiry = Date.now() + 3600 * 1000;

      // Guardar en estado Y en localStorage
      setAccessToken(token);
      setTokenExpiry(expiry);
      setIsConnected(true);
      localStorage.setItem(STORAGE_KEY_TOKEN, token);
      localStorage.setItem(STORAGE_KEY_EXPIRY, String(expiry));

      if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = setTimeout(() => {
        handleTokenExpired();
      }, 3600 * 1000);

      toast.success('Google Calendar conectado');
      return token;
    } catch (error) {
      console.error('Error connecting Google Calendar:', error);
      if (error.code === 'auth/popup-closed-by-user') {
        toast.error('Conexión cancelada');
      } else if (error.code === 'auth/popup-blocked') {
        toast.error('Popup bloqueado por el navegador');
      } else {
        toast.error('Error al conectar Google Calendar');
        setSyncError(error.message);
      }
      return null;
    } finally {
      setIsConnecting(false);
    }
  }, [handleTokenExpired]);

  const disconnectCalendar = useCallback(() => {
    setAccessToken(null);
    setTokenExpiry(null);
    setIsConnected(false);
    setGoogleEvents([]);
    setLastSyncTime(null);
    setSyncError(null);
    localStorage.removeItem(STORAGE_KEY_TOKEN);
    localStorage.removeItem(STORAGE_KEY_EXPIRY);
    if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
    toast.success('Google Calendar desconectado');
  }, []);

  const syncEvents = useCallback(async (timeMin, timeMax) => {
    if (!accessToken || isTokenExpired()) {
      setSyncError('Token expirado. Reconecta Google Calendar.');
      setIsConnected(false);
      return [];
    }

    try {
      setSyncError(null);
      const events = await fetchGoogleCalendarEvents(accessToken, timeMin, timeMax);
      setGoogleEvents(events);
      setLastSyncTime(new Date());
      return events;
    } catch (error) {
      console.error('Error syncing Google Calendar:', error);
      if (error.status === 401) {
        setIsConnected(false);
        setAccessToken(null);
        localStorage.removeItem(STORAGE_KEY_TOKEN);
        localStorage.removeItem(STORAGE_KEY_EXPIRY);
        setSyncError('Token inválido. Reconecta Google Calendar.');
      } else {
        setSyncError('Error al sincronizar: ' + error.message);
      }
      return [];
    }
  }, [accessToken, isTokenExpired]);

  const value = {
    isConnected,
    isConnecting,
    googleEvents,
    lastSyncTime,
    syncError,
    accessToken,
    connectCalendar,
    disconnectCalendar,
    syncEvents,
    isTokenExpired,
  };

  return (
    <GoogleCalendarContext.Provider value={value}>
      {children}
    </GoogleCalendarContext.Provider>
  );
};
